/**
 * Mic button — drives connect / disconnect.
 *
 * Three visible states:
 *  - idle / error → static glass disc, mic icon
 *  - connecting / disconnecting → pulsing ring while transitioning
 *  - connected → animated aura + live audio-level halo
 */

import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { tokens } from "./styles";

// Sizes are parameterised because the panel renders a compact variant
// when the viewport is short (≤720px tall). Footer must stay visible
// without scrolling — see NokiCopilotPanel useIsCompactViewport().
const SIZE_FULL = 72;
const SIZE_COMPACT = 56;
const INNER_INSET_FULL = 8;
const INNER_INSET_COMPACT = 6;
const ICON_FULL = 22;
const ICON_COMPACT = 18;

const Icon = ({ size = 22, strokeWidth = 1.8, children }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const MicIcon = (
  <Icon>
    <path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 1 0 6 0V5a3 3 0 0 0-3-3z" />
    <path d="M19 11a7 7 0 1 1-14 0" />
    <path d="M12 18v3" />
    <path d="M9 21h6" />
  </Icon>
);

const StopIcon = (
  <Icon>
    <rect x="6.5" y="6.5" width="11" height="11" rx="2" />
  </Icon>
);

export function MicButton({ status, levelRef, onToggle, compact = false }) {
  const connected = status === "connected";
  const transitioning = status === "connecting" || status === "disconnecting";
  const errored = status === "error";

  // The live audio-level halo scales with the ~30Hz level signal. Paint it
  // IMPERATIVELY from the ref via rAF (DOM transform, no setState) so the level
  // never re-renders this button — same reason as AudioVisualizer.
  const haloRef = useRef(null);
  useEffect(() => {
    const el = haloRef.current;
    if (el === null) return undefined;
    if (!connected) {
      el.style.transform = "scale(1)";
      return undefined;
    }
    let running = true;
    let raf = 0;
    const tick = () => {
      if (!running) return;
      const raw = levelRef && typeof levelRef.current === "number" ? levelRef.current : 0;
      el.style.transform = `scale(${1 + Math.min(Math.max(raw, 0), 1) * 0.18})`;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      running = false;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [connected, levelRef]);

  const size = compact ? SIZE_COMPACT : SIZE_FULL;
  const innerInset = compact ? INNER_INSET_COMPACT : INNER_INSET_FULL;
  const iconSize = compact ? ICON_COMPACT : ICON_FULL;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={connected}
      aria-label={connected ? "End voice session" : "Start voice session"}
      style={{
        position: "relative",
        width: size,
        height: size,
        borderRadius: "50%",
        border: "none",
        cursor: transitioning ? "progress" : "pointer",
        background: "transparent",
        padding: 0,
        outline: "none",
      }}
    >
      <span
        ref={haloRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          opacity: connected ? 1 : 0.55,
          transform: "scale(1)",
          // Imperative scale (set from the level ref via rAF); CSS smooths it.
          transition: "transform 90ms ease-out, opacity 240ms ease",
          background: connected
            ? `radial-gradient(circle at 30% 30%, ${tokens.accent.cyan}55, ${tokens.accent.violet}66 60%, transparent 75%)`
            : `radial-gradient(circle at 30% 30%, ${tokens.surface.borderStrong}, transparent 70%)`,
          boxShadow: connected ? tokens.shadow.glow : tokens.shadow.button,
        }}
      />
      {transitioning ? (
        <motion.span
          aria-hidden="true"
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, ease: "linear", repeat: Infinity }}
          style={{
            position: "absolute",
            inset: -2,
            borderRadius: "50%",
            border: `1.5px solid ${tokens.accent.cyan}`,
            borderTopColor: "transparent",
            opacity: 0.85,
          }}
        />
      ) : null}
      <span
        style={{
          position: "absolute",
          inset: innerInset,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          background: errored
            ? "rgba(220, 38, 38, 0.22)"
            : "rgba(15, 23, 42, 0.7)",
          border: `1px solid ${tokens.surface.borderStrong}`,
          color: errored ? "#FCA5A5" : tokens.ink[50],
          backdropFilter: "blur(6px)",
        }}
      >
        {React.cloneElement(connected ? StopIcon : MicIcon, { size: iconSize })}
      </span>
    </button>
  );
}
